import {useId} from 'react';
import {Box, useComputedColorScheme, useMantineTheme} from '@mantine/core';

/**
 * Hero backdrop for the studio page.
 *
 * Drawn rather than photographed, for the same reasons as the article artwork:
 * it costs nothing to serve, is correct in both colour schemes, and scales to
 * any viewport without a second asset. The figure is the studio thesis — one
 * shared core with four ventures orbiting it.
 *
 * Purely decorative: aria-hidden, sits behind the hero content, and never
 * intercepts pointer events.
 */
const StudioBackdrop = () => {
    const theme = useMantineTheme();
    const isDark = useComputedColorScheme('light') === 'dark';
    const uid = useId().replace(/:/g, '');

    const ink = isDark ? theme.colors.brand[3] : theme.colors.brand[6];
    const warm = isDark ? theme.colors.accent[3] : theme.colors.accent[2];

    // Deliberately faint. This sits under headline text, so contrast has to
    // survive it — the whole figure reads at roughly 10% opacity.
    const line = isDark ? 0.28 : 0.22;
    const glow = isDark ? 0.5 : 0.32;

    // Four satellites, evenly spaced, matching the four ventures.
    const satellites = [
        {x: 250, y: 120},
        {x: 640, y: 90},
        {x: 300, y: 330},
        {x: 700, y: 320}
    ];

    return (
        <Box
            aria-hidden
            style={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
                // Faded on both axes. Without the horizontal fade the backdrop
                // stops dead at the container's max-width and reads as a
                // lighter rectangle pasted onto the page.
                maskImage:
                    'linear-gradient(to bottom, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%),' +
                    ' linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%,' +
                    ' rgba(0,0,0,1) 78%, rgba(0,0,0,0) 100%)',
                maskComposite: 'intersect',
                WebkitMaskImage:
                    'linear-gradient(to bottom, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%),' +
                    ' linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%,' +
                    ' rgba(0,0,0,1) 78%, rgba(0,0,0,0) 100%)',
                WebkitMaskComposite: 'source-in'
            }}
        >
            <svg
                width="100%"
                height="100%"
                viewBox="0 0 960 420"
                preserveAspectRatio="xMaxYMin slice"
                style={{display: 'block'}}
            >
                <defs>
                    <radialGradient id={`aura-${uid}`} cx="60%" cy="35%" r="55%">
                        <stop offset="0%" stopColor={ink} stopOpacity={glow * 0.5}/>
                        <stop offset="100%" stopColor={ink} stopOpacity="0"/>
                    </radialGradient>
                    <radialGradient id={`warm-${uid}`} cx="88%" cy="80%" r="45%">
                        <stop offset="0%" stopColor={warm} stopOpacity={glow * 0.4}/>
                        <stop offset="100%" stopColor={warm} stopOpacity="0"/>
                    </radialGradient>
                    <filter id={`soft-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="6"/>
                    </filter>
                </defs>

                <rect width="960" height="420" fill={`url(#aura-${uid})`}/>
                <rect width="960" height="420" fill={`url(#warm-${uid})`}/>

                <g stroke={ink} fill="none" strokeLinecap="round">
                    <circle cx={480} cy={210} r={128} opacity={line * 0.45} strokeWidth={1}/>
                    <circle cx={480} cy={210} r={196} opacity={line * 0.3} strokeWidth={1}/>
                    <circle cx={480} cy={210} r={268} opacity={line * 0.18} strokeWidth={1}/>

                    {satellites.map((s, i) => (
                        <path
                            key={`edge-${i}`}
                            d={`M480 210 L${s.x} ${s.y}`}
                            strokeWidth={1.1}
                            opacity={line * 0.6}
                        />
                    ))}

                    <g filter={`url(#soft-${uid})`} opacity={glow * 0.6}>
                        <circle cx={480} cy={210} r={26} fill={ink} stroke="none"/>
                    </g>
                    <circle cx={480} cy={210} r={11} fill={ink} stroke="none" opacity={line * 2.2}/>

                    {satellites.map((s, i) => (
                        <g key={`node-${i}`}>
                            <circle cx={s.x} cy={s.y} r={16} opacity={line * 0.5} strokeWidth={1}/>
                            <circle cx={s.x} cy={s.y} r={6} fill={ink} stroke="none" opacity={line * 1.8}/>
                        </g>
                    ))}
                </g>
            </svg>
        </Box>
    );
};

export default StudioBackdrop;
